import api from './api';
import type {
  BuyWithCoinsResult,
  PaginatedTransactionHistory,
  RedeemWalletCodeResult,
  StudentTransactionHistory,
  Transaction,
} from '../types/wallet';
import { CONTENT_MESSAGES } from '../utils/contentMessages';

interface ApiEnvelope {
  success?: boolean;
  Success?: boolean;
  message?: string | object;
  Message?: string | object;
  data?: unknown;
  Data?: unknown;
}

function isSuccess(body: ApiEnvelope): boolean {
  return body.success === true || body.Success === true;
}

function getErrorMessage(body: ApiEnvelope, fallback: string): string {
  const message = body.message ?? body.Message;
  if (typeof message === 'string' && message) return message;
  const data = body.data ?? body.Data;
  if (typeof data === 'string' && data) return data;
  return fallback;
}

function unwrapPayload(body: ApiEnvelope): Record<string, unknown> | null {
  if (!isSuccess(body)) return null;
  const message = body.message ?? body.Message;
  const data = body.data ?? body.Data;
  if (message && typeof message === 'object') return message as Record<string, unknown>;
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  return null;
}

function normalizeTransaction(raw: Record<string, unknown>): Transaction {
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    studentId: String(raw.studentId ?? raw.StudentId ?? ''),
    studentName: String(raw.studentName ?? raw.StudentName ?? ''),
    type: String(raw.type ?? raw.Type ?? ''),
    amount: Number(raw.amount ?? raw.Amount ?? 0),
    balanceAfter: Number(raw.balanceAfter ?? raw.BalanceAfter ?? 0),
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
    description: (raw.description ?? raw.Description ?? undefined) as string | undefined,
    itemId: raw.itemId != null || raw.ItemId != null ? String(raw.itemId ?? raw.ItemId) : undefined,
    itemType: (raw.itemType ?? raw.ItemType ?? undefined) as Transaction['itemType'],
    itemPrice: raw.itemPrice != null || raw.ItemPrice != null ? Number(raw.itemPrice ?? raw.ItemPrice) : undefined,
  };
}

function normalizeHistory(raw: Record<string, unknown>): StudentTransactionHistory {
  const transactionsRaw = (raw.transactions ?? raw.Transactions ?? []) as Record<string, unknown>[];
  return {
    studentId: String(raw.studentId ?? raw.StudentId ?? ''),
    studentName: String(raw.studentName ?? raw.StudentName ?? ''),
    currentBalance: Number(raw.currentBalance ?? raw.CurrentBalance ?? 0),
    totalTransactions: Number(raw.totalTransactions ?? raw.TotalTransactions ?? 0),
    transactions: Array.isArray(transactionsRaw) ? transactionsRaw.map(normalizeTransaction) : [],
  };
}

export const walletService = {
  async getMyHistory(pageNumber = 1, pageSize = 10) {
    try {
      const response = await api.get<ApiEnvelope>('/api/Transaction/my-history', {
        params: { pageNumber, pageSize },
      });
      const body = response.data;
      const payload = unwrapPayload(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'تعذر تحميل سجل المحفظة') };
      }

      const historyRaw = (payload.data ?? payload.Data ?? payload) as Record<string, unknown>;
      const history = normalizeHistory(historyRaw);

      return {
        success: true as const,
        data: {
          history,
          totalCount: Number(payload.totalCount ?? payload.TotalCount ?? history.totalTransactions),
          pageNumber: Number(payload.pageNumber ?? payload.PageNumber ?? pageNumber),
          pageSize: Number(payload.pageSize ?? payload.PageSize ?? pageSize),
          totalPages: Number(payload.totalPages ?? payload.TotalPages ?? 1),
        } satisfies PaginatedTransactionHistory,
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiEnvelope } };
      const message = err.response?.data
        ? getErrorMessage(err.response.data, 'تعذر تحميل سجل المحفظة')
        : 'تعذر تحميل سجل المحفظة';
      return { success: false as const, error: message };
    }
  },

  async redeemWalletCode(code: string) {
    try {
      const response = await api.post<ApiEnvelope>('/api/WalletCode/redeem', { code: code.trim() });
      const body = response.data;
      const payload = unwrapPayload(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'فشل في استخدام كود المحفظة') };
      }

      const result: RedeemWalletCodeResult = {
        previousBalance: Number(payload.previousBalance ?? payload.PreviousBalance ?? 0),
        addedCoins: Number(payload.addedCoins ?? payload.AddedCoins ?? 0),
        newBalance: Number(payload.newBalance ?? payload.NewBalance ?? 0),
        transactionId: String(payload.transactionId ?? payload.TransactionId ?? ''),
      };

      return { success: true as const, data: result };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiEnvelope } };
      const message = err.response?.data
        ? getErrorMessage(err.response.data, 'فشل في استخدام كود المحفظة')
        : 'فشل في استخدام كود المحفظة';
      return { success: false as const, error: message };
    }
  },

  async getBalance() {
    const result = await this.getMyHistory(1, 1);
    if (!result.success) {
      return { success: false as const, error: result.error };
    }
    return { success: true as const, balance: result.data.history.currentBalance };
  },

  async buyCourse(courseId: string, promoCode?: string) {
    try {
      const response = await api.post<ApiEnvelope>('/api/Coins/buy-course', {
        courseId,
        promoCode: promoCode?.trim() || undefined,
      });
      const body = response.data;
      const payload = unwrapPayload(body);
      if (!payload) {
        return {
          success: false as const,
          error: mapBuyError(getErrorMessage(body, 'فشل شراء الكورس من المحفظة')),
        };
      }

      return { success: true as const, data: normalizeBuyResult(payload, 'course') };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiEnvelope } };
      const message = err.response?.data
        ? getErrorMessage(err.response.data, 'فشل شراء الكورس من المحفظة')
        : 'فشل شراء الكورس من المحفظة';
      return { success: false as const, error: mapBuyError(message) };
    }
  },

  async buyUnit(unitId: number, promoCode?: string) {
    try {
      const response = await api.post<ApiEnvelope>('/api/Coins/buy-unit', {
        unitId,
        promoCode: promoCode?.trim() || undefined,
      });
      const body = response.data;
      const payload = unwrapPayload(body);
      if (!payload) {
        return {
          success: false as const,
          error: mapBuyError(getErrorMessage(body, 'فشل شراء الحصة من المحفظة')),
        };
      }

      return { success: true as const, data: normalizeBuyResult(payload, 'unit') };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiEnvelope } };
      const message = err.response?.data
        ? getErrorMessage(err.response.data, 'فشل شراء الحصة من المحفظة')
        : 'فشل شراء الحصة من المحفظة';
      return { success: false as const, error: mapBuyError(message) };
    }
  },
};

function normalizeBuyResult(
  payload: Record<string, unknown>,
  kind: 'course' | 'unit'
): BuyWithCoinsResult {
  const itemName =
    kind === 'course'
      ? String(payload.courseName ?? payload.CourseName ?? '')
      : String(payload.unitName ?? payload.UnitName ?? '');

  return {
    orderId: String(payload.orderId ?? payload.OrderId ?? ''),
    transactionId: String(payload.transactionId ?? payload.TransactionId ?? ''),
    itemName,
    originalPrice: Number(payload.originalPrice ?? payload.OriginalPrice ?? 0),
    discountAmount: Number(payload.discountAmount ?? payload.DiscountAmount ?? 0),
    netPrice: Number(payload.netPrice ?? payload.NetPrice ?? 0),
    coinsSpent: Number(payload.coinsSpent ?? payload.CoinsSpent ?? 0),
    previousBalance: Number(payload.previousBalance ?? payload.PreviousBalance ?? 0),
    newBalance: Number(payload.newBalance ?? payload.NewBalance ?? 0),
    promoCodeApplied: Boolean(payload.promoCodeApplied ?? payload.PromoCodeApplied ?? false),
  };
}

function mapBuyError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('insufficient coins')) {
    const required = message.match(/Required:\s*(\d+)/i)?.[1];
    const available = message.match(/Available:\s*(\d+)/i)?.[1];
    if (required && available) {
      return `رصيدك غير كافٍ. المطلوب ${required} عملة، والمتاح ${available} عملة. اشحن محفظتك ثم حاول مرة أخرى.`;
    }
    return 'رصيدك غير كافٍ لشراء هذا الكورس. اشحن محفظتك ثم حاول مرة أخرى.';
  }
  if (lower.includes('already enrolled')) {
    return CONTENT_MESSAGES.alreadyEnrolled;
  }
  if (lower.includes('not active')) {
    return CONTENT_MESSAGES.contentUnavailable;
  }
  if (lower.includes('not found') && lower.includes('course')) {
    return CONTENT_MESSAGES.courseNotFound;
  }
  if (lower.includes('not found') && lower.includes('unit')) {
    return CONTENT_MESSAGES.unitNotFound.title;
  }
  if (lower.includes('promocode')) {
    return 'كود الخصم غير صالح.';
  }
  if (lower.includes('not authenticated') || lower.includes('unauthorized')) {
    return 'يرجى تسجيل الدخول أولاً.';
  }
  return message;
}
